import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './api/queryClient';

// Import console cleanup (must be early to catch all logs)
import './utils/consoleCleanup';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { UIProvider } from './contexts/UIContext';
import { ConnectionProvider } from './contexts/EnhancedConnectionContext';
import { BillingProvider } from './contexts/BillingContext';

// Auth pages
import LoginPage from './pages/Login/LoginPage';
import SignUpPage from './pages/Auth/SignUpPage';
import RegisterPage from './pages/Login/RegisterPage';
import ForgotPasswordPage from './pages/Login/ForgotPasswordPage';
import ResetPasswordPage from './pages/Login/ResetPasswordPage';
import AcceptInvite from './pages/Auth/AcceptInvite';

// Layout
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages
import DashboardPage from './pages/Dashboard/DashboardPage';
import ConnectionsPage from './pages/Connections/ConnectionsPage';
import ConnectionDetailPage from './pages/Connections/ConnectionDetailPage';
import NewConnectionPage from './pages/Connections/NewConnectionPage';
import DataExplorerPage from './pages/DataExplorer/DataExplorerPage';
import TableDetailPage from './pages/DataExplorer/TableDetailPage';
import ValidationPage from './pages/Validations/ValidationPage';
import MetadataPage from './pages/Metadata/MetadataPage';

// Analytics pages
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import TableAnalyticsPage from './pages/Analytics/TableAnalyticsPage';
import SchemaChangesPage from './pages/Analytics/SchemaChangesPage';
import BusinessImpactPage from './pages/Analytics/BusinessImpactPage';

// Unified Monitors (replaces legacy /anomalies + /metrics pages).
// Legacy routes redirect here via RedirectToMonitors (defined after the
// import block); the old page files are removed in Step 16 of the
// monitors unification refactor.
import MonitorsPage from './pages/Monitors/MonitorsPage';
import MonitorForm from './pages/Monitors/MonitorForm';

// Settings: Automation
import AutomationSettingsPage from './pages/Settings/AutomationSettingsPage';

// Settings pages
import UserSettingsPage from './pages/Settings/UserSettingsPage';
import StakeholderGroupDetailPage from './pages/Settings/StakeholderGroupDetailPage';

// Admin pages
import AdminSettingsPage from './pages/Admin/AdminSettingsPage';

// Onboarding pages
import OnboardingWelcomePage from './pages/Onboarding/OnboardingWelcomePage';
import OnboardingConnectionPage from './pages/Onboarding/OnboardingConnectionPage';
import OnboardingSchemaSelectionPage from './pages/Onboarding/OnboardingSchemaSelectionPage';
import OnboardingValidationPage from './pages/Onboarding/OnboardingValidationPage';
import OnboardingResultsPage from './pages/Onboarding/OnboardingResultsPage';
import OnboardingDashboardPage from './pages/Onboarding/OnboardingDashboardPage';

// Welcome flow pages
import WelcomePage from './pages/Welcome/WelcomePage';
import WelcomeDataPage from './pages/Welcome/WelcomeDataPage';
import WelcomeFeaturesPage from './pages/Welcome/WelcomeFeaturesPage';
import WelcomeTasksPage from './pages/Welcome/WelcomeTasksPage';
import WelcomeCompletePage from './pages/Welcome/WelcomeCompletePage';

// Onboarding Router
import OnboardingRouter from './components/onboarding/OnboardingRouter';

// Issues and Lineage pages
import IssuesPage from './pages/Issues/IssuesPage';
import IssueDetailPage from './pages/Issues/IssueDetailPage';
import LineagePage from './pages/Lineage/LineagePage';
import LineageObjectPage from './pages/Lineage/LineageObjectPage';

// Notifications page
import NotificationsPage from './pages/Notifications/NotificationsPage';

// Documentation page
import DocumentationPage from './pages/Documentation/DocumentationPage';

// dbt pages
import DbtDashboardPage from './pages/Dbt/DbtDashboardPage';
import DbtConnectionsPage from './pages/Dbt/DbtConnectionsPage';
import NewDbtConnectionPage from './pages/Dbt/NewDbtConnectionPage';
import DbtModelsPage from './pages/Dbt/DbtModelsPage';
import DbtTestsPage from './pages/Dbt/DbtTestsPage';

// Other pages
import NotFoundPage from './pages/NotFoundPage';

// Bookmark-friendly redirect for the legacy /anomalies/* and /metrics/*
// URLs. Preserves :connectionSlug so a deep link like
// /anomalies/snowflake-prod/configs lands the user on
// /monitors/snowflake-prod rather than the bare /monitors index.
// Old metric/config IDs aren't in the new monitor ID space so we drop
// them — user re-finds the monitor in the list.
const RedirectToMonitors = () => {
  const { connectionSlug } = useParams();
  return (
    <Navigate
      to={connectionSlug ? `/monitors/${connectionSlug}` : '/monitors'}
      replace
    />
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <BillingProvider>
            <UIProvider>
              <ConnectionProvider>
                <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/accept-invite/:token" element={<AcceptInvite />} />

                {/* Onboarding routes - Protected but outside main layout */}
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <OnboardingWelcomePage />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/welcome" element={
                  <ProtectedRoute>
                    <OnboardingWelcomePage />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/connection" element={
                  <ProtectedRoute>
                    <OnboardingConnectionPage />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/tables" element={
                  <ProtectedRoute>
                    <OnboardingSchemaSelectionPage />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/validation-generation" element={
                  <ProtectedRoute>
                    <OnboardingValidationPage />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/results" element={
                  <ProtectedRoute>
                    <OnboardingResultsPage />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/dashboard" element={
                  <ProtectedRoute>
                    <OnboardingDashboardPage />
                  </ProtectedRoute>
                } />

                {/* Welcome flow routes - Protected but outside main layout */}
                <Route path="/welcome" element={
                  <ProtectedRoute>
                    <WelcomePage />
                  </ProtectedRoute>
                } />
                <Route path="/welcome/data" element={
                  <ProtectedRoute>
                    <WelcomeDataPage />
                  </ProtectedRoute>
                } />
                <Route path="/welcome/features" element={
                  <ProtectedRoute>
                    <WelcomeFeaturesPage />
                  </ProtectedRoute>
                } />
                <Route path="/welcome/tasks" element={
                  <ProtectedRoute>
                    <WelcomeTasksPage />
                  </ProtectedRoute>
                } />
                <Route path="/welcome/complete" element={
                  <ProtectedRoute>
                    <WelcomeCompletePage />
                  </ProtectedRoute>
                } />

                {/* Protected routes */}
                <Route element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }>
                  {/* Dashboard */}
                  <Route path="/dashboard" element={
                    <OnboardingRouter>
                      <DashboardPage />
                    </OnboardingRouter>
                  } />

                  {/* Connections */}
                  <Route path="/connections" element={<ConnectionsPage />} />
                  <Route path="/connections/new" element={<NewConnectionPage />} />
                  <Route path="/connections/:connectionSlug" element={<ConnectionDetailPage />} />

                  {/* Data Explorer */}
                  <Route path="/explorer" element={<DataExplorerPage />} />
                  <Route path="/explorer/:connectionSlug/tables/:tableName" element={<TableDetailPage />} />

                  {/* Validations */}
                  <Route path="/validations" element={<ValidationPage />} />

                  {/* Metadata */}
                  <Route path="/metadata" element={<MetadataPage />} />

                  {/* Issues & Lineage */}
                  <Route path="/issues" element={<IssuesPage />} />
                  <Route path="/issues/:issueId" element={<IssueDetailPage />} />
                  <Route path="/lineage" element={<LineagePage />} />
                  <Route path="/lineage/table/:tableName" element={<LineageObjectPage />} />

                  {/* Notifications */}
                  <Route path="/notifications" element={<NotificationsPage />} />

                  {/* Legacy Anomaly Detection routes — redirect to unified /monitors.
                      Step 15 of monitors unification (2026-05-19). Old page
                      components removed in Step 16. */}
                  <Route path="/anomalies" element={<RedirectToMonitors />} />
                  <Route path="/anomalies/:connectionSlug" element={<RedirectToMonitors />} />
                  <Route path="/anomalies/:connectionSlug/explorer" element={<RedirectToMonitors />} />
                  <Route path="/anomalies/:connectionSlug/configs" element={<RedirectToMonitors />} />
                  <Route path="/anomalies/:connectionSlug/configs/:configId" element={<RedirectToMonitors />} />

                  {/* Analytics */}
                  <Route path="/analytics" element={<AnalyticsPage />} />
                  <Route path="/analytics/table/:connectionSlug/:tableName" element={<TableAnalyticsPage />} />
                  <Route path="/analytics/schema-changes/:connectionSlug" element={<SchemaChangesPage />} />
                  <Route path="/analytics/business-impact/:connectionSlug" element={<BusinessImpactPage />} />

                  {/* Legacy Custom Metrics routes — redirect to unified /monitors. */}
                  <Route path="/metrics" element={<RedirectToMonitors />} />
                  <Route path="/metrics/:metricId" element={<RedirectToMonitors />} />

                  {/* Unified Monitors — replaces both /anomalies and /metrics. */}
                  <Route path="/monitors" element={<MonitorsPage />} />
                  <Route path="/monitors/:connectionSlug" element={<MonitorsPage />} />
                  <Route path="/monitors/:connectionSlug/new" element={<MonitorForm />} />
                  <Route path="/monitors/:connectionSlug/:monitorId" element={<MonitorForm />} />

                  {/* Documentation */}
                  <Route path="/documentation" element={<DocumentationPage />} />

                  {/* dbt Integration */}
                  <Route path="/dbt" element={<DbtConnectionsPage />} />
                  <Route path="/dbt/connections" element={<DbtConnectionsPage />} />
                  <Route path="/dbt/connections/new" element={<NewDbtConnectionPage />} />
                  <Route path="/dbt/projects/:projectId" element={<DbtDashboardPage />} />
                  <Route path="/dbt/projects/:projectId/models" element={<DbtModelsPage />} />
                  <Route path="/dbt/projects/:projectId/tests" element={<DbtTestsPage />} />

                  {/* Settings - User Level */}
                  <Route path="/settings" element={<UserSettingsPage />} />
                  <Route path="/settings/profile" element={<UserSettingsPage />} />
                  <Route path="/settings/billing" element={<UserSettingsPage />} />
                  <Route path="/settings/api-keys" element={<UserSettingsPage />} />
                  <Route path="/settings/automation" element={<AutomationSettingsPage />} />
                  <Route path="/settings/validation-issues" element={<UserSettingsPage />} />
                  <Route path="/settings/preferences" element={<UserSettingsPage />} />
                  <Route path="/settings/security" element={<UserSettingsPage />} />

                  {/* Admin - Organization Level */}
                  <Route path="/admin" element={<AdminSettingsPage />} />
                  <Route path="/admin/users" element={<AdminSettingsPage />} />
                  <Route path="/admin/teams" element={<AdminSettingsPage />} />
                  <Route path="/admin/stakeholder-groups" element={<AdminSettingsPage />} />
                  <Route path="/admin/stakeholder-groups/:groupId" element={<StakeholderGroupDetailPage />} />
                  <Route path="/admin/notifications" element={<AdminSettingsPage />} />
                  <Route path="/admin/snoozed-alerts" element={<AdminSettingsPage />} />
                  <Route path="/admin/alert-routing" element={<AdminSettingsPage />} />
                  <Route path="/admin/notification-test" element={<AdminSettingsPage />} />
                  <Route path="/admin/organization" element={<AdminSettingsPage />} />
                  <Route path="/admin/query-history" element={<AdminSettingsPage />} />
                  <Route path="/admin/cost-monitoring" element={<AdminSettingsPage />} />
                  <Route path="/admin/query-performance" element={<AdminSettingsPage />} />

                  {/* Root redirect */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />

                  {/* 404 */}
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
              </ConnectionProvider>
            </UIProvider>
          </BillingProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;